import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Search, ExternalLink, BookOpen, Image, ZoomIn, X } from 'lucide-react';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredContent, setFilteredContent] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  // Image modal component
  const ImageModal = ({ src, alt, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-6xl max-h-full">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 p-2"
        >
          <X size={20} />
        </button>
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-3 rounded-b-lg">
          <p className="text-xs">{alt}</p>
        </div>
      </div>
    </div>
  );

  // Enhanced screenshot component with zoom functionality
  const Screenshot = ({ title, alt, size = 'large', className = '', src }) => (
    <div className={`my-4 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden ${className}`}>
      <div className="p-3 bg-gray-50 border-b border-gray-300">
        <h4 className="font-medium text-sm text-gray-800 flex items-center justify-between">
          {title}
          <ZoomIn size={14} className="text-gray-500" />
        </h4>
      </div>
      <div className={`relative group cursor-pointer ${size === 'large' ? 'min-h-80' : 'min-h-40'}`}>
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <Image className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <p className="text-gray-500 font-medium text-sm">{title}</p>
            <p className="text-gray-400 text-xs mt-1">Click to view full size</p>
          </div>
        </div>
        <img 
          src={src} 
          alt={alt}
          className="hidden w-full h-full object-cover transition-transform group-hover:scale-105"
          onClick={() => setSelectedImage({ src: src, alt })}
          onLoad={(e) => {
            e.target.parentElement.querySelector('.absolute').classList.add('hidden');
            e.target.classList.remove('hidden');
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
    </div>
  );

  const sections = [
    {
      id: 'overview',
      title: 'Platform Overview',
      content: (
        <div className="space-y-6">
          <div className="prose prose-sm max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to AutoML.ai</h2>
            <p className="text-base text-gray-700 leading-relaxed">
              AutoML.ai is a revolutionary no-code machine learning platform that democratizes AI development. 
              Build, train, and deploy sophisticated ML models through an intuitive visual interface—no programming experience required.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">🚀 Key Benefits</h3>
                <ul className="space-y-1 text-blue-700 text-sm">
                  <li>• Zero coding required</li>
                  <li>• Drag-and-drop pipeline builder</li>
                  <li>• Automated model optimization</li>
                  <li>• Real-time collaboration</li>
                  <li>• Enterprise-grade security</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-2">⚡ Core Features</h3>
                <ul className="space-y-1 text-green-700 text-sm">
                  <li>• 15+ ML algorithms</li>
                  <li>• Interactive data visualizations</li>
                  <li>• Model explanations & insights</li>
                  <li>• API deployment</li>
                  <li>• Performance monitoring</li>
                </ul>
              </div>
            </div>
          </div>

          <Screenshot 
            title="Platform Dashboard Overview"
            alt="Main dashboard showing project overview, recent activity, and system status"
            size="large"
            src="/images/dashboard.png"
          />

          <div className="">
            <Screenshot 
              title="Project Management Interface"
              alt="Projects page with create, manage, and organize functionality"
              size="medium"
              src="/images/projects.png"
            />
            
          </div>
        </div>
      ),
      searchableText: `Platform Overview AutoML.ai no-code machine learning platform democratizes AI development build train deploy ML models visual interface programming zero coding drag-and-drop pipeline builder automated model optimization real-time collaboration enterprise security algorithms data visualizations model explanations insights API deployment performance monitoring dashboard project management`
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      content: (
        <div className="space-y-6">
          <div className="prose prose-sm max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Start Guide</h2>
            <p className="text-base text-gray-700 leading-relaxed">
              Get up and running with your first ML model in under 10 minutes. Follow this step-by-step guide 
              to create your first project and build a complete machine learning pipeline.
            </p>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">🎯 Your First ML Project</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-lg font-bold">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">Create Project</h4>
                <p className="text-xs text-gray-600 mt-1">Set up your workspace and define your ML objectives</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-lg font-bold">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">Build Pipeline</h4>
                <p className="text-xs text-gray-600 mt-1">Drag and drop components to create your ML workflow</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-lg font-bold">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">Deploy Model</h4>
                <p className="text-xs text-gray-600 mt-1">Launch your model and start making predictions</p>
              </div>
            </div>
          </div>

          <Screenshot 
            
          title="Template Selection"
                alt="Gallery of pre-built pipeline templates for different use cases"
                size="medium"
                src="/images/template.png"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">📋 Detailed Steps</h3>
              <div className="space-y-3">
                {[{
                  step: 1,
                  title: "Create a New Project",
                  desc: "Click 'New Project' and choose from our templates or start from scratch"
                },
                {
                  step: 2,
                  title: "Upload Your Data",
                  desc: "Drag and drop CSV files or connect to databases and cloud storage"
                },
                {
                  step: 3,
                  title: "Select Template",
                  desc: "Choose from pre-built pipelines or create a custom workflow"
                },
                {
                  step: 4,
                  title: "Configure Features",
                  desc: "Select input features and target variables for your model"
                },
                {
                  step: 5,
                  title: "Train Your Model",
                  desc: "Let AutoML find the best algorithm and hyperparameters"
                },
                {
                  step: 6,
                  title: "Evaluate Results",
                  desc: "Review performance metrics and model explanations"
                }].map(({ step, title, desc }) => (
                  <div key={step} className="flex items-start space-x-3 p-3 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-semibold text-xs">{step}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">{title}</h4>
                      <p className="text-xs text-gray-600 mt-1">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <Screenshot 
                title="Project Creation Wizard"
            alt="Step-by-step project creation interface with templates and configuration options"
            size="large"
            src="/images/create-project.png"
                
              />
              <Screenshot 
                title="Data Upload Interface"
                alt="Drag and drop data upload with preview and validation"
                size="medium"
                src="/images/upload-dataset.png"
              />
            </div>
          </div>
        </div>
      ),
      searchableText: `Getting Started Quick Start Guide first ML model minutes step-by-step project pipeline Create Project workspace ML objectives Build Pipeline drag drop components workflow Deploy Model launch predictions Project Creation Wizard templates configuration Upload Data CSV files databases cloud storage Select Template pre-built pipelines custom workflow Configure Features input features target variables Train Model AutoML algorithm hyperparameters Evaluate Results performance metrics model explanations`
    },
    {
      id: 'data-management',
      title: 'Data Management',
      content: (
        <div className="space-y-6">
          <div className="prose prose-sm max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sources & Management</h2>
            <p className="text-base text-gray-700 leading-relaxed">
              AutoML.ai supports multiple data sources and provides powerful tools for data preparation, 
              cleaning, and transformation. Learn how to efficiently manage your datasets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[{
              icon: "📁",
              title: "File Upload",
              desc: "CSV, Excel, JSON, Parquet files",
              color: "blue"
            },
            {
              icon: "🗄️",
              title: "Kaggle Datasets",
              desc: "Based on keyword search",
              color: "green"
            },
            {
              icon: "☁️",
              title: "Cloud Storage",
              desc: "To be added soon",
              color: "purple"
            }
            
            ].map(({ icon, title, desc, color }) => (
              <div key={title} className={`p-4 bg-${color}-50 border border-${color}-200 rounded-lg`}>
                <div className="text-2xl mb-2">{icon}</div>
                <h3 className={`text-base font-semibold text-${color}-800 mb-1`}>{title}</h3>
                <p className={`text-xs text-${color}-700`}>{desc}</p>
              </div>
            ))}
          </div>

          <Screenshot 
            title="Kaggle Connection Manager"
            alt="Interface for connecting to various datasets in kaggle based on a keyword search"
            size="large"
            src="/images/kaggle.png"
          />

          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">🔧 Data Preparation Tools</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-base font-semibold text-gray-800 mb-3">Cleaning & Validation</h4>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Automatic data type detection</li>
                  <li>• Missing value identification</li>
                  <li>• Outlier detection and handling</li>
                  
                </ul>
              </div>
              <div>
                <h4 className="text-base font-semibold text-gray-800 mb-3">Transformation</h4>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Feature encoding and scaling</li>
                  <li>• Data preprocessing</li>
                  <li>• Column merging</li>
                  <li>• Custom transformations</li>
                </ul>
              </div>
            </div>
          </div>

          
        </div>
      ),
      searchableText: `Data Management Data Sources multiple data sources data preparation cleaning transformation datasets File Upload CSV Excel JSON Parquet files Databases MySQL PostgreSQL MongoDB Cloud Storage AWS S3 Google Cloud Azure APIs REST APIs GraphQL endpoints Enterprise Salesforce SAP Oracle Streaming Kafka real-time data feeds Data Source Connection Manager authentication configuration Data Preparation Tools Cleaning Validation automatic data type detection missing value identification outlier detection handling duplicate record removal data quality scoring Transformation feature encoding scaling date time parsing text preprocessing column splitting merging custom transformations Data Preview Profiling statistics data types quality indicators Data Cleaning Interface missing values outliers data quality issues`
    },
    {
      id: 'pipeline-builder',
      title: 'Pipeline Builder',
      content: (
        <div className="space-y-6">
          <div className="prose prose-sm max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Visual Pipeline Builder</h2>
            <p className="text-base text-gray-700 leading-relaxed">
              Create sophisticated machine learning workflows using our intuitive drag-and-drop interface. 
              Build complex pipelines without writing a single line of code.
            </p>
          </div>

          <Screenshot 
            title="Pipeline Builder Canvas"
            alt="Full pipeline builder interface showing nodes, connections, and configuration panels"
            size="large"
            src={"/images/pipelinecanvas.png"}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[{
              category: "Data Input",
              nodes: ["Upload Dataset", "Database Connection", "API Import"],
              color: "blue"
            },
            {
              category: "Preprocessing",
              nodes: ["Clean Data", "Feature Selection", "Scaling"],
              color: "green"
            },
            {
              category: "Modeling",
              nodes: ["Train Model", "Ensemble", "Hyperparameter Tuning"],
              color: "purple"
            },
            {
              category: "Output",
              nodes: ["Evaluate", "Analyze", "Export"],
              color: "orange"
            }].map(({ category, nodes, color }) => (
              <div key={category} className={`p-4 bg-${color}-50 border border-${color}-200 rounded-lg`}>
                <h3 className={`text-base font-semibold text-${color}-800 mb-3`}>{category}</h3>
                <div className="space-y-1">
                  {nodes.map(node => (
                    <div key={node} className={`p-2 bg-white border border-${color}-200 rounded-md text-xs`}>
                      {node}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border border-indigo-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">🎨 Building Your Pipeline</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-white text-sm">🎯</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">Drag & Drop</h4>
                  <p className="text-xs text-gray-600 mt-1">Simply drag nodes from the palette to the canvas</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-white text-sm">🔗</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">Connect</h4>
                  <p className="text-xs text-gray-600 mt-1">Link nodes to create data flow relationships</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-white text-sm">⚙️</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">Configure</h4>
                  <p className="text-xs text-gray-600 mt-1">Set parameters and options for each component</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Screenshot 
              title="Node Configuration Panel"
              alt="Detailed configuration interface for individual pipeline nodes"
              size="medium"
              src="/images/nodes.png"
            />
            <Screenshot 
              title="Pipeline Execution Monitor"
              alt="Real-time pipeline execution with progress indicators and logs"
              size="medium"
              src="/images/execution.png"
            />
          </div>
        </div>
      ),
      searchableText: `Pipeline Builder Visual Pipeline Builder sophisticated machine learning workflows intuitive drag-and-drop interface complex pipelines without writing code Pipeline Builder Canvas nodes connections configuration panels Data Input Upload Dataset Database Connection API Import Preprocessing Clean Data Feature Selection Scaling Modeling Train Model Ensemble Hyperparameter Tuning Output Evaluate Deploy Export Building Pipeline Drag Drop nodes palette canvas Connect Link nodes data flow relationships Configure parameters options component Node Configuration Panel individual pipeline nodes Pipeline Execution Monitor real-time pipeline execution progress indicators logs`
    },
    {
      id: 'models-training',
      title: 'Model Training',
      content: (
        <div className="space-y-6">
          <div className="prose prose-sm max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">AI-Powered Model Training</h2>
            <p className="text-base text-gray-700 leading-relaxed">
              Leverage automated machine learning to find the best algorithms and hyperparameters for your data. 
              Our platform supports 15+ algorithms with intelligent optimization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">🎯 Classification</h3>
              <ul className="space-y-1 text-blue-700 text-sm">
                <li>• Random Forest</li>
                <li>• Gradient Boosting</li>
                <li>• SVM</li>
                <li>• Neural Networks</li>
                <li>• Logistic Regression</li>
              </ul>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-green-800 mb-3">📈 Regression</h3>
              <ul className="space-y-1 text-green-700 text-sm">
                <li>• Linear Regression</li>
                <li>• Ridge & Lasso</li>
                <li>• Random Forest</li>
                <li>• XGBoost</li>
                <li>• Support Vector Regression</li>
              </ul>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="text-lg font-semibold text-purple-800 mb-3">🔍 Clustering</h3>
              <ul className="space-y-1 text-purple-700 text-sm">
                <li>• K-Means</li>
                <li>• DBSCAN</li>
                <li>• Hierarchical</li>
                <li>• Gaussian Mixture</li>
                <li>• Spectral Clustering</li>
              </ul>
            </div>
          </div>

          <Screenshot 
            title="AutoML Training Interface"
            alt="Automated model training with algorithm selection and hyperparameter optimization"
            size="large"
            src={"/images/execution.png"}
          />

          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">🤖 AutoML Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-base font-semibold text-gray-800 mb-3">Intelligent Optimization</h4>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Automated algorithm selection</li>
                  <li>• Hyperparameter tuning</li>
                  <li>• Cross-validation</li>
                  <li>• Early stopping</li>
                  <li>• Ensemble modeling</li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-semibold text-gray-800 mb-3">Performance Tracking</h4>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Real-time metrics</li>
                  <li>• Training progress</li>
                  <li>• Resource utilization</li>
                  <li>• Model comparison</li>
                  <li>• Performance history</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Screenshot 
              title="Model Explainability Dashboard"
              alt="Dashboard showing model performance metrics and explanations"
              size="medium"
              src={"/images/explanations.png"}
            />
            <Screenshot 
              title="Hyperparameter Optimization"
              alt="Interactive hyperparameter tuning interface with optimization results"
              size="medium"
              src={"/images/hyperparameters.png"}
            />
          </div>

          <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-lg border border-orange-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">⚡ Training Strategies</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-1 text-sm">Quick Train</h4>
                <p className="text-xs text-gray-600">Fast training with default parameters for rapid prototyping</p>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-1 text-sm">Optimize Mode</h4>
                <p className="text-xs text-gray-600">Advanced optimization for maximum performance</p>
              </div>
            </div>
          </div>
        </div>
      ),
      searchableText: `Model Training AI-Powered Model Training automated machine learning algorithms hyperparameters 15+ algorithms intelligent optimization Classification Random Forest Gradient Boosting SVM Neural Networks Logistic Regression Regression Linear Regression Ridge Lasso Random Forest XGBoost Support Vector Regression Clustering K-Means DBSCAN Hierarchical Gaussian Mixture Spectral Clustering AutoML Training Interface automated model training algorithm selection hyperparameter optimization AutoML Features Intelligent Optimization automated algorithm selection hyperparameter tuning cross-validation early stopping ensemble modeling Performance Tracking real-time metrics training progress resource utilization model comparison performance history Model Comparison Dashboard side-by-side comparison models performance metrics Hyperparameter Optimization interactive hyperparameter tuning optimization results Training Strategies Quick Train fast training default parameters rapid prototyping Optimize Mode advanced optimization maximum performance`
    },
    {
      id: 'analytics-visualization',
      title: 'Analytics & Visualization',
      content: (
        <div className="space-y-6">
          <div className="prose prose-sm max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Advanced Analytics & Insights</h2>
            <p className="text-base text-gray-700 leading-relaxed">
              Gain deep insights into your data and models with interactive visualizations, 
              comprehensive analytics, and explainable AI features.
            </p>
          </div>

          <Screenshot 
            title="Analytics Dashboard"
            alt="Comprehensive analytics dashboard with multiple charts and performance indicators"
            size="large"
            src={"/images/analytics.png"}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[{
              type: "Data Exploration",
              features: ["Distribution plots", "Correlation matrix", "Feature importance", "Missing data patterns"],
              color: "blue"
            },
            {
              type: "Model Performance",
              features: ["ROC curves", "Confusion matrix", "Precision-recall", "Learning curves"],
              color: "green"
            },
            {
              type: "Predictions",
              features: ["Prediction intervals", "Residual plots", "Forecast charts", "Anomaly detection"],
              color: "purple"
            }].map(({ type, features, color }) => (
              <div key={type} className={`p-4 bg-${color}-50 border border-${color}-200 rounded-lg`}>
                <h3 className={`text-base font-semibold text-${color}-800 mb-3`}>{type}</h3>
                <ul className="space-y-1">
                  {features.map(feature => (
                    <li key={feature} className={`text-xs text-${color}-700`}>• {feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg border border-indigo-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">🔍 Explainable AI</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-base font-semibold text-gray-800 mb-3">Model Interpretability</h4>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• SHAP value explanations</li>
                  <li>• Feature importance rankings</li>
                  <li>• Decision tree visualization</li>
                  <li>• Partial dependence plots</li>
                  <li>• LIME explanations</li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-semibold text-gray-800 mb-3">Business Insights</h4>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Prediction confidence scores</li>
                  <li>• Key driver analysis</li>
                  <li>• What-if scenarios</li>
                  <li>• Business impact metrics</li>
                  <li>• Recommendation engine</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Screenshot 
              title="Feature Importance Analysis"
              alt="Interactive feature importance charts with explanations and insights"
              size="medium"
              src="/images/featureimportance.png"
            />
            <Screenshot 
              title="Model Explainability Dashboard"
              alt="SHAP values, LIME explanations, and model interpretation tools"
              size="medium"
              src="/images/explanations.png"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">📊 Interactive Charts</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Screenshot 
                title="Correlation Heatmap"
                alt="Interactive correlation matrix with hover details and filtering"
                size="medium"
                src="/images/heatmap.png"
              />
              <Screenshot 
                title="Distribution Plots"
                alt="Feature distribution histograms with statistical overlays"
                size="medium"
                src="/images/distributions.png"
              />
            </div>
          </div>

        </div>
      ),
      searchableText: `Analytics Visualization Advanced Analytics Insights deep insights data models interactive visualizations comprehensive analytics explainable AI features Analytics Dashboard charts performance indicators Data Exploration Distribution plots Correlation matrix Feature importance Missing data patterns Model Performance ROC curves Confusion matrix Precision-recall Learning curves Predictions Prediction intervals Residual plots Forecast charts Anomaly detection Explainable AI Model Interpretability SHAP value explanations Feature importance rankings Decision tree visualization Partial dependence plots LIME explanations Business Insights Prediction confidence scores Key driver analysis What-if scenarios Business impact metrics Recommendation engine Feature Importance Analysis interactive feature importance charts explanations insights Model Explainability Dashboard SHAP values LIME explanations model interpretation tools Interactive Charts Correlation Heatmap interactive correlation matrix hover details filtering Distribution Plots Feature distribution histograms statistical overlays Performance Metrics Comparison model performance comparison multiple metrics visualization options`
    }
  ];

  // Enhanced search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredContent([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = [];

    sections.forEach(section => {
      const titleMatch = section.title.toLowerCase().includes(query);
      const contentMatch = section.searchableText.toLowerCase().includes(query);
      
      if (titleMatch || contentMatch) {
        results.push({
          section: section.id,
          title: section.title,
          relevance: titleMatch ? 10 : 5
        });
      }
    });

    // Sort by relevance
    results.sort((a, b) => b.relevance - a.relevance);
    setFilteredContent(results);
  }, [searchQuery]);

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    setSearchQuery(''); // Clear search when navigating
  };

  const activeContent = sections.find(section => section.id === activeSection);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          src={selectedImage.src}
          alt={selectedImage.alt}
          onClose={() => setSelectedImage(null)}
        />
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 py-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-3">
                  <BookOpen size={24} className="text-blue-600 mr-3" />
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    Documentation
                  </h1>
                </div>
                <p className="text-base text-gray-600 max-w-2xl">
                  Complete guide to building, training, and deploying machine learning models with AutoML.ai
                </p>
              </div>
            </div>
            
            {/* Enhanced Search */}
            <div className="mt-6 relative max-w-xl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 text-sm border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all duration-200"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              
              {/* Search Results Dropdown */}
              {searchQuery && filteredContent.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <div className="p-1">
                    {filteredContent.slice(0, 5).map(({ section, title }) => (
                      <button
                        key={section}
                        onClick={() => handleSectionClick(section)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md transition-colors"
                      >
                        <div className="font-medium text-gray-900 text-sm">{title}</div>
                        <div className="text-xs text-gray-500">Click to view section</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-white border-r border-gray-200 min-h-screen">
            <div className="p-4">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Table of Contents</h2>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 flex items-center ${
                      activeSection === section.id
                        ? 'bg-blue-100 text-blue-700 border-l-2 border-blue-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <ChevronRight 
                      size={12} 
                      className={`mr-2 transition-transform ${
                        activeSection === section.id ? 'rotate-90' : ''
                      }`} 
                    />
                    {section.title}
                  </button>
                ))}
              </nav>
              
              {/* External Resources */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-xs font-semibold text-gray-900 mb-3">External Resources</h3>
                <div className="space-y-2">
                  {[{
                    name: "Scikit-learn Docs",
                    url: "https://scikit-learn.org/stable/"
                  },
                  {
                    name: "Pandas Documentation",
                    url: "https://pandas.pydata.org/docs/"
                  },
                  {
                    name: "API Reference",
                    url: "#"
                  },
                  {
                    name: "Community Forum",
                    url: "#"
                  }].map(({ name, url }) => (
                    <a
                      key={name}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-xs text-blue-600 hover:text-blue-800 group"
                    >
                      <span>{name}</span>
                      <ExternalLink size={10} className="ml-1 transition-transform group-hover:translate-x-0.5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 bg-white">
            <div className="p-6">
              {activeContent && (
                <div className="max-w-3xl">
                  {activeContent.content}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
